import { compactFromDay, type DayNumber } from "@/lib/tool-kit/study/dates";

/**
 * An iCalendar (RFC 5545) writer for all-day events — the planners' "Add to calendar".
 *
 * Google Calendar, Outlook and Apple Calendar all import a `.ics` file, and all three
 * are strict in different places, so this writes the conservative subset every one of
 * them accepts:
 *
 * - **CRLF line endings**, and a final CRLF (§3.1).
 * - **Lines folded at 75 octets** — octets, not characters, so a line of Japanese folds
 *   sooner than a line of English — with each continuation starting with one space, and
 *   never in the middle of a UTF-8 sequence (§3.1).
 * - **TEXT escaping**: backslash, semicolon and comma escaped, newlines written as `\n`
 *   (§3.3.11). An unescaped comma in a SUMMARY is the most common way a hand-rolled
 *   `.ics` breaks, and the break is silent: the title is cut at the comma.
 * - **`UID` and `DTSTAMP` on every VEVENT** — both are required (§3.6.1).
 * - **All-day dates as `VALUE=DATE`** with an exclusive `DTEND` (the day after the last
 *   day), which is how all three apps read an all-day event. A floating date has no time
 *   zone, so an exam on the 14th is on the 14th wherever the calendar is opened.
 * - **`TRANSP:TRANSPARENT`**, so a reading goal does not mark the whole day as busy.
 */

export interface IcsAllDayEvent {
  /** Globally unique and stable for this event. */
  uid: string;
  /** The first day of the event. */
  start: DayNumber;
  /** The last day, inclusive. Defaults to `start` — a one-day event. */
  end?: DayNumber;
  summary: string;
  description?: string;
}

export interface IcsCalendar {
  /** `-//Organisation//Product//EN`, per §3.7.3. */
  prodId: string;
  /** Shown as the calendar's name by apps that read `X-WR-CALNAME`. */
  name?: string;
  /** The moment the file was made; becomes every event's `DTSTAMP`. */
  stamp: Date;
  events: readonly IcsAllDayEvent[];
}

const BACKSLASH = /\\/g;
const SEMICOLON = /;/g;
const COMMA = /,/g;
const NEWLINE = /\r\n|\r|\n/g;
const MAX_LINE_OCTETS = 75;

/** Escape a TEXT value (§3.3.11). The backslash goes first, or it escapes the escapes. */
export function escapeIcsText(value: string): string {
  return value
    .replace(BACKSLASH, "\\\\")
    .replace(SEMICOLON, "\\;")
    .replace(COMMA, "\\,")
    .replace(NEWLINE, "\\n");
}

function utf8Octets(char: string): number {
  const code = char.codePointAt(0) ?? 0;
  if (code < 0x80) {
    return 1;
  }
  if (code < 0x8_00) {
    return 2;
  }
  if (code < 0x1_00_00) {
    return 3;
  }
  return 4;
}

/**
 * Fold one content line to 75 octets (§3.1).
 *
 * The first physical line carries 75 octets; every continuation is a space plus 74. The
 * loop walks code points, so a surrogate pair or a multi-byte character is never split.
 */
export function foldIcsLine(line: string): string {
  const parts: string[] = [];
  let current = "";
  let octets = 0;
  let limit = MAX_LINE_OCTETS;
  for (const char of line) {
    const size = utf8Octets(char);
    if (octets + size > limit) {
      parts.push(current);
      current = "";
      octets = 0;
      limit = MAX_LINE_OCTETS - 1;
    }
    current += char;
    octets += size;
  }
  parts.push(current);
  return parts.join("\r\n ");
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** A UTC DATE-TIME, `YYYYMMDDTHHMMSSZ` (§3.3.5, form 2). */
export function icsUtcStamp(date: Date): string {
  return `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(
    date.getUTCDate()
  )}T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(
    date.getUTCSeconds()
  )}Z`;
}

function eventLines(event: IcsAllDayEvent, stamp: string): string[] {
  const last = event.end ?? event.start;
  const lines = [
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(event.uid)}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${compactFromDay(event.start)}`,
    // Exclusive: an event on the 14th ends at the start of the 15th.
    `DTEND;VALUE=DATE:${compactFromDay(Math.max(last, event.start) + 1)}`,
    `SUMMARY:${escapeIcsText(event.summary)}`,
  ];
  if (event.description) {
    lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  }
  lines.push("TRANSP:TRANSPARENT", "END:VEVENT");
  return lines;
}

/** A complete VCALENDAR document, folded and CRLF-terminated. */
export function buildIcsCalendar(calendar: IcsCalendar): string {
  const stamp = icsUtcStamp(calendar.stamp);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${escapeIcsText(calendar.prodId)}`,
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];
  if (calendar.name) {
    lines.push(`X-WR-CALNAME:${escapeIcsText(calendar.name)}`);
  }
  for (const event of calendar.events) {
    lines.push(...eventLines(event, stamp));
  }
  lines.push("END:VCALENDAR");
  return `${lines.map(foldIcsLine).join("\r\n")}\r\n`;
}

/**
 * UIDs for one export: `<token>-<n>@citationeasy.com`.
 *
 * A fresh token per download, on purpose. Re-importing an edited plan with the same UIDs
 * makes some calendars keep the old copy and silently skip the new dates; fresh UIDs
 * make a second import visible as a second set of events, which the reader can delete.
 */
export function icsUidFactory(token: string): (index: number) => string {
  return (index) => `${token}-${index + 1}@citationeasy.com`;
}

/** The MIME type for `downloadText`. */
export const ICS_MIME = "text/calendar;charset=utf-8";
